import React, { useEffect } from 'react';
import { Card, Row, Col, Statistic, Progress, Spin, Alert, Tooltip, Button } from 'antd';
import { QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getSchoolClassroomStatistics } from '../../core/school/schoolApi';
import { AppDispatch, CombinedStates } from '../../core/types';
import { SchoolClassroomStatistics } from '../../core/school/types';
import './style.scss';

interface SchoolClassroomStatisticProps {
    schoolId?: number | null;
    mode?: 'overall' | 'school' | 'default';
    title?: string;
    statistics?: SchoolClassroomStatistics | null;
}

const SchoolClassroomStatistic: React.FC<SchoolClassroomStatisticProps> = ({
    schoolId,
    mode = 'default',
    title = 'Klasių statistika',
    statistics,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const {
        classroomStatistics,
        overallClassroomStatistics,
        isFetching,
        error,
    } = useSelector((state: CombinedStates) => state.school);

    // Determine which statistics to use based on mode
    const getStatisticsData = (): SchoolClassroomStatistics | null => {
        if (statistics) {
            return statistics;
        }

        switch (mode) {
        case 'overall':
            return overallClassroomStatistics || null;
        case 'school':
            return classroomStatistics || null;
        case 'default':
            return classroomStatistics || null;
        default:
            return classroomStatistics || null;
        }
    };

    const currentStatistics = getStatisticsData();

    const formatClassCount = (count: number): string => {
        if (count === 0) return 'klasių';
        if (count === 1) return 'klasė';
        if (count >= 2 && count <= 9) return 'klasės';
        return 'klasių';
    };

    const formatGroupCount = (count: number): string => {
        if (count === 0) return 'grupių';
        if (count === 1) return 'grupė';
        if (count >= 2 && count <= 9) return 'grupės';
        return 'grupių';
    };

    const fetchStatistics = () => {
        if (mode === 'default' && schoolId) {
            dispatch(getSchoolClassroomStatistics({ school_id: schoolId }));
        }
    };

    useEffect(() => {
        fetchStatistics();
    }, [dispatch, schoolId, mode]);

    // Show loading only for default mode when fetching from server
    const shouldShowLoading = mode === 'default' && isFetching && !currentStatistics;

    if (shouldShowLoading) {
        return (
            <div className="school-classroom-statistic">
                <Card>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: '10px' }}>Kraunama klasės statistika...</p>
                    </div>
                </Card>
            </div>
        );
    }

    if (error && mode === 'default') {
        return (
            <div className="school-classroom-statistic">
                <Card>
                    <Alert
                        message="Klaida"
                        description="Nepavyko užkrauti klasės statistikos"
                        type="error"
                        showIcon />
                </Card>
            </div>
        );
    }

    if (!currentStatistics) {
        return (
            <div className="school-classroom-statistic">
                <Card>
                    <div style={{ textAlign: 'right' }}>
                        {mode === 'default' && (
                            <Tooltip title="Atnaujinti duomenis">
                                <Button
                                    icon={<ReloadOutlined spin={isFetching} />}
                                    onClick={fetchStatistics}
                                    disabled={isFetching}
                                    style={{ color: '#fdab01' }} />
                            </Tooltip>
                        )}
                    </div>
                    <br />
                    <Alert
                        message="Informacija"
                        description="Klasės statistika nerasta"
                        type="info"
                        showIcon />
                </Card>
            </div>
        );
    }

    const {
        school_type,
        total_classrooms,
        total_students,
        total_physical_students,
        average_students_per_classroom,
        total_ukrainian_children,
        total_foreign_children,
        total_diaspora_children,
        total_sup_children,
        average_ukrainian_per_classroom,
        total_classrooms_1_4,
        total_classrooms_5_8,
        total_classrooms_i_ii,
        total_classrooms_iii_iv,
        classrooms_1_4_large_class_percentage,
        classrooms_5_8_large_class_percentage,
        classrooms_i_ii_large_class_percentage,
        classrooms_iii_iv_large_class_percentage,
        classrooms_less_than_8_percentage,
        classrooms_less_than_8_count,
        classrooms_1_4_large_class_count,
        classrooms_5_8_large_class_count,
        classrooms_i_ii_large_class_count,
        classrooms_iii_iv_large_class_count,
        classrooms_1_4_max_24_count,
        classrooms_5_8_max_30_count,
        classrooms_i_ii_max_30_count,
        classrooms_iii_iv_max_30_count,
        classrooms_1_4_max_24_percentage,
        classrooms_5_8_max_30_percentage,
        classrooms_i_ii_max_30_percentage,
        classrooms_iii_iv_max_30_percentage,
        large_class_threshold_1_4,
        large_class_threshold_5_8,
        large_class_threshold_i_ii,
        large_class_threshold_iii_iv,
        classroom_type_statistics,
    } = currentStatistics;

    return (
        <div className="school-classroom-statistic">

            {school_type !== 'darzelis' ? (
                <Card
                    title={(
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{title}</span>
                            {mode === 'default' && (
                                <Tooltip title="Atnaujinti duomenis">
                                    <Button
                                        icon={<ReloadOutlined spin={isFetching} />}
                                        onClick={fetchStatistics}
                                        disabled={isFetching}
                                        style={{ color: '#fdab01' }} />
                                </Tooltip>
                            )}
                        </div>
                    )}
                    style={{ marginBottom: '16px' }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Iš viso klasių"
                                value={total_classrooms}
                                suffix={formatClassCount(total_classrooms)} />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Iš viso mokinių"
                                value={total_students}
                                suffix="mokinių" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Faktinių mokinių"
                                value={total_physical_students}
                                suffix="mokinių" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Vidutiniškai mokinių"
                                value={average_students_per_classroom}
                                precision={1}
                                suffix="mokinių/klasė" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Mažų klasių (<8 mok.)"
                                value={classrooms_less_than_8_count}
                                suffix={`klasių (${classrooms_less_than_8_percentage.toFixed(1)}%)`} />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Ukrainiečių vaikų"
                                value={total_ukrainian_children}
                                suffix="vaikų" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Vidutiniškai Ukrainiečių"
                                value={average_ukrainian_per_classroom}
                                precision={1}
                                suffix="vaikų/klasė" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Užsienio vaikų"
                                value={total_foreign_children}
                                suffix="vaikų" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Diasporos vaikų"
                                value={total_diaspora_children}
                                suffix="vaikų" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="SUP vaikų"
                                value={total_sup_children}
                                suffix="vaikų" />
                        </Col>
                    </Row>
                </Card>
            ) : (
                <Card
                    title={(
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{title}</span>
                            {mode === 'default' && (
                                <Tooltip title="Atnaujinti duomenis">
                                    <Button
                                        icon={<ReloadOutlined spin={isFetching} />}
                                        onClick={fetchStatistics}
                                        disabled={isFetching}
                                        style={{ color: '#fdab01' }} />
                                </Tooltip>
                            )}
                        </div>
                    )}
                    style={{ marginBottom: '16px' }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Iš viso grupių"
                                value={total_classrooms}
                                suffix={formatGroupCount(total_classrooms)} />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Iš viso mokinių"
                                value={total_students}
                                suffix="mokinių" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Faktinių mokinių"
                                value={total_physical_students}
                                suffix="mokinių" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Vidutiniškai mokinių"
                                value={average_students_per_classroom}
                                precision={1}
                                suffix="mokinių/grupė" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Mažų grupių (<8 mok.)"
                                value={classrooms_less_than_8_count}
                                suffix={`grupių (${classrooms_less_than_8_percentage.toFixed(1)}%)`} />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Ukrainiečių vaikų"
                                value={total_ukrainian_children}
                                suffix="vaikų" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Vidutiniškai Ukrainiečių"
                                value={average_ukrainian_per_classroom}
                                precision={1}
                                suffix="vaikų/grupė" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Užsienio vaikų"
                                value={total_foreign_children}
                                suffix="vaikų" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="Diasporos vaikų"
                                value={total_diaspora_children}
                                suffix="vaikų" />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Statistic
                                title="SUP vaikų"
                                value={total_sup_children}
                                suffix="vaikų" />
                        </Col>

                    </Row>
                </Card>
            )}

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    {school_type !== 'darzelis' && (
                        <Card title="Klasės pagal tipą" size="small">
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Statistic
                                        title="1-4 klasės"
                                        value={total_classrooms_1_4}
                                        suffix={formatClassCount(total_classrooms_1_4)} />
                                    <Progress
                                        percent={classrooms_1_4_large_class_percentage}
                                        size="small"
                                        status={classrooms_1_4_large_class_percentage > 50 ? 'exception' : 'normal'}
                                        format={(percent) => `${percent?.toFixed(1)}% didelių klasių`} />
                                </Col>
                                <Col span={12}>
                                    <Statistic
                                        title="5-8 klasės"
                                        value={total_classrooms_5_8}
                                        suffix={formatClassCount(total_classrooms_5_8)} />
                                    <Progress
                                        percent={classrooms_5_8_large_class_percentage}
                                        size="small"
                                        status={classrooms_5_8_large_class_percentage > 50 ? 'exception' : 'normal'}
                                        format={(percent) => `${percent?.toFixed(1)}% didelių klasių`} />
                                </Col>
                                <Col span={12}>
                                    <Statistic
                                        title="I-II klasės"
                                        value={total_classrooms_i_ii}
                                        suffix={formatClassCount(total_classrooms_i_ii)} />
                                    <Progress
                                        percent={classrooms_i_ii_large_class_percentage}
                                        size="small"
                                        status={classrooms_i_ii_large_class_percentage > 50 ? 'exception' : 'normal'}
                                        format={(percent) => `${percent?.toFixed(1)}% didelių klasių`} />
                                </Col>
                                <Col span={12}>
                                    <Statistic
                                        title="III-IV klasės"
                                        value={total_classrooms_iii_iv}
                                        suffix={formatClassCount(total_classrooms_iii_iv)} />
                                    <Progress
                                        percent={classrooms_iii_iv_large_class_percentage}
                                        size="small"
                                        status={classrooms_iii_iv_large_class_percentage > 50 ? 'exception' : 'normal'}
                                        format={(percent) => `${percent?.toFixed(1)}% didelių klasių`} />
                                </Col>
                            </Row>
                        </Card>
                    )}
                </Col>

                {school_type !== 'darzelis' && (
                    <Col xs={24} lg={12}>
                        <Card title="Didelių klasių analizė" size="small">
                            <div style={{ padding: '8px 0' }}>
                                <p><strong>Didelių klasių procentai:</strong></p>
                                <ul style={{ paddingLeft: '20px' }}>
                                    <li>
                                        1-4 klasės: <strong>{classrooms_1_4_large_class_percentage.toFixed(1)}%</strong> ({classrooms_1_4_large_class_count} {formatClassCount(classrooms_1_4_large_class_count)})
                                        <Tooltip title={`Didelė klasė: >= ${parseFloat(large_class_threshold_1_4).toFixed(0)} mokinių`}>
                                            <QuestionCircleOutlined style={{ marginLeft: '8px', color: '#fdab01', cursor: 'pointer' }} />
                                        </Tooltip>
                                    </li>
                                    <li>
                                        5-8 klasės: <strong>{classrooms_5_8_large_class_percentage.toFixed(1)}%</strong> ({classrooms_5_8_large_class_count} {formatClassCount(classrooms_5_8_large_class_count)})
                                        <Tooltip title={`Didelė klasė: >= ${parseFloat(large_class_threshold_5_8).toFixed(0)} mokinių`}>
                                            <QuestionCircleOutlined style={{ marginLeft: '8px', color: '#fdab01', cursor: 'pointer' }} />
                                        </Tooltip>
                                    </li>
                                    <li>
                                        I-II klasės: <strong>{classrooms_i_ii_large_class_percentage.toFixed(1)}%</strong> ({classrooms_i_ii_large_class_count} {formatClassCount(classrooms_i_ii_large_class_count)})
                                        <Tooltip title={`Didelė klasė: >= ${parseFloat(large_class_threshold_i_ii).toFixed(0)} mokinių`}>
                                            <QuestionCircleOutlined style={{ marginLeft: '8px', color: '#fdab01', cursor: 'pointer' }} />
                                        </Tooltip>
                                    </li>
                                    <li>
                                        III-IV klasės: <strong>{classrooms_iii_iv_large_class_percentage.toFixed(1)}%</strong> ({classrooms_iii_iv_large_class_count} {formatClassCount(classrooms_iii_iv_large_class_count)})
                                        <Tooltip title={`Didelė klasė: >= ${parseFloat(large_class_threshold_iii_iv).toFixed(0)} mokinių`}>
                                            <QuestionCircleOutlined style={{ marginLeft: '8px', color: '#fdab01', cursor: 'pointer' }} />
                                        </Tooltip>
                                    </li>
                                </ul>

                                <p style={{ marginTop: '16px' }}><strong>Klasės atitinkančios maksimalius dydžius:</strong></p>
                                <div style={{ paddingLeft: '20px' }}>
                                    <div style={{ marginBottom: '12px' }}>
                                        <div>1-4 klasės: {classrooms_1_4_max_24_count} {formatClassCount(classrooms_1_4_max_24_count)} ({classrooms_1_4_max_24_percentage.toFixed(1)}%) - ne daugiau kaip 24 mokiniai</div>
                                        <Progress
                                            percent={classrooms_1_4_max_24_percentage}
                                            size="small"
                                            status={classrooms_1_4_max_24_percentage >= 80 ? 'success' : 'normal'}
                                            format={(percent) => `${percent?.toFixed(1)}% atitinka standartą`} />
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <div>5-8 klasės: {classrooms_5_8_max_30_count} {formatClassCount(classrooms_5_8_max_30_count)} ({classrooms_5_8_max_30_percentage.toFixed(1)}%) - ne daugiau kaip 30 mokinių</div>
                                        <Progress
                                            percent={classrooms_5_8_max_30_percentage}
                                            size="small"
                                            status={classrooms_5_8_max_30_percentage >= 80 ? 'success' : 'normal'}
                                            format={(percent) => `${percent?.toFixed(1)}% atitinka standartą`} />
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <div>I-II klasės: {classrooms_i_ii_max_30_count} {formatClassCount(classrooms_i_ii_max_30_count)} ({classrooms_i_ii_max_30_percentage.toFixed(1)}%) - ne daugiau kaip 30 mokinių</div>
                                        <Progress
                                            percent={classrooms_i_ii_max_30_percentage}
                                            size="small"
                                            status={classrooms_i_ii_max_30_percentage >= 80 ? 'success' : 'normal'}
                                            format={(percent) => `${percent?.toFixed(1)}% atitinka standartą`} />
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <div>III-IV klasės: {classrooms_iii_iv_max_30_count} {formatClassCount(classrooms_iii_iv_max_30_count)} ({classrooms_iii_iv_max_30_percentage.toFixed(1)}%) - ne daugiau kaip 30 mokinių</div>
                                        <Progress
                                            percent={classrooms_iii_iv_max_30_percentage}
                                            size="small"
                                            status={classrooms_iii_iv_max_30_percentage >= 80 ? 'success' : 'normal'}
                                            format={(percent) => `${percent?.toFixed(1)}% atitinka standartą`} />
                                    </div>
                                </div>

                                <p style={{ marginTop: '16px', fontSize: '12px', color: '#666' }}>
                                    <em>Didelėmis klasėmis laikomos klasės, kuriose mokinių skaičius viršija nustatytą ribą. Maksimalių dydžių klasės - klasės, kurios atitinka rekomenduojamus mokinių skaičius.</em>
                                </p>
                            </div>
                        </Card>
                    </Col>
                )}
            </Row>

            {/* School Type Statistics Section - Only for overall mode */}
            {mode === 'overall' && currentStatistics.school_type_statistics && (
                <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                    <Col xs={24}>
                        <Card title="Statistika pagal mokyklos tipą" size="small">
                            <Row gutter={[16, 16]}>
                                {Object.entries(currentStatistics.school_type_statistics).map(([key, stats]) => {
                                    // Only show if there are classrooms of this school type
                                    if (stats.total_classrooms === 0) {
                                        return null;
                                    }

                                    const isKindergarten = stats.school_type === 'darzelis';

                                    return (
                                        <Col xs={24} sm={12} md={8} lg={6} key={key}>
                                            <Card size="small" style={{ height: '100%' }}>
                                                <Statistic
                                                    title={stats.school_type_name}
                                                    value={stats.total_classrooms}
                                                    suffix={isKindergarten ? formatGroupCount(stats.total_classrooms) : formatClassCount(stats.total_classrooms)} />
                                                <div style={{ marginTop: '8px' }}>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        Mokinių: {stats.total_students}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        Faktinių: {stats.total_physical_students}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        Vidutiniškai: {stats.average_students_per_classroom.toFixed(1)} mok./{isKindergarten ? 'grupė' : 'klasė'}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        Mažų {isKindergarten ? 'grupių' : 'klasių'} (&lt;8 mok.): {stats.classrooms_less_than_8_count} ({stats.classrooms_less_than_8_percentage.toFixed(1)}%)
                                                    </div>
                                                    {stats.total_ukrainian_children > 0 && (
                                                        <div style={{ fontSize: '12px', color: '#1890ff' }}>
                                                            Ukrainiečių: {stats.total_ukrainian_children}
                                                        </div>
                                                    )}
                                                    {stats.total_foreign_children > 0 && (
                                                        <div style={{ fontSize: '12px', color: '#52c41a' }}>
                                                            Užsienio: {stats.total_foreign_children}
                                                        </div>
                                                    )}
                                                    {stats.total_diaspora_children > 0 && (
                                                        <div style={{ fontSize: '12px', color: '#fa8c16' }}>
                                                            Diasporos: {stats.total_diaspora_children}
                                                        </div>
                                                    )}
                                                    {stats.total_sup_children > 0 && (
                                                        <div style={{ fontSize: '12px', color: '#722ed1' }}>
                                                            SUP: {stats.total_sup_children}
                                                        </div>
                                                    )}
                                                </div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Classroom Type Statistics Section */}
            {classroom_type_statistics && (
                <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                    <Col xs={24}>
                        <Card title="Klasės pagal tipą" size="small">
                            <Row gutter={[16, 16]}>
                                {Object.entries(classroom_type_statistics).map(([key, stats]) => {
                                    // For kindergarten schools, only show types 13-18
                                    if (mode !== 'overall' && school_type === 'darzelis' && (stats.classroom_type < 13 || stats.classroom_type > 18)) {
                                        return null;
                                    }

                                    // For regular schools, show types 1-12
                                    if (mode !== 'overall' && school_type !== 'darzelis' && (stats.classroom_type < 1 || stats.classroom_type > 12)) {
                                        return null;
                                    }

                                    // Only show if there are classrooms of this type
                                    if (stats.total_classrooms === 0) {
                                        return null;
                                    }

                                    let isisKindergarten = false;
                                    if (stats.classroom_type > 12) {
                                        isisKindergarten = true;
                                    }

                                    return (
                                        <Col xs={24} sm={12} md={8} lg={6} key={key}>
                                            <Card size="small" style={{ height: '100%' }}>
                                                <Statistic
                                                    title={stats.classroom_type_name}
                                                    value={stats.total_classrooms}
                                                    suffix={isisKindergarten ? formatGroupCount(stats.total_classrooms) : formatClassCount(stats.total_classrooms)} />
                                                <div style={{ marginTop: '8px' }}>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        Mokinių: {stats.total_students}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        Faktinių vaikų sk.: {stats.total_physical_students}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        Vidutiniškai: {stats.average_students_per_classroom.toFixed(1)} mok./{isisKindergarten ? 'grupė' : 'klasė'}
                                                    </div>
                                                    {stats.total_ukrainian_children > 0 && (
                                                        <div style={{ fontSize: '12px', color: '#1890ff' }}>
                                                            Ukrainiečių: {stats.total_ukrainian_children}
                                                        </div>
                                                    )}
                                                    {stats.total_foreign_children > 0 && (
                                                        <div style={{ fontSize: '12px', color: '#52c41a' }}>
                                                            Užsienio: {stats.total_foreign_children}
                                                        </div>
                                                    )}
                                                    {stats.total_diaspora_children > 0 && (
                                                        <div style={{ fontSize: '12px', color: '#fa8c16' }}>
                                                            Diasporos: {stats.total_diaspora_children}
                                                        </div>
                                                    )}
                                                    {stats.total_sup_children > 0 && (
                                                        <div style={{ fontSize: '12px', color: '#722ed1' }}>
                                                            SUP: {stats.total_sup_children}
                                                        </div>
                                                    )}
                                                </div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
};

SchoolClassroomStatistic.defaultProps = {
    schoolId: null,
    mode: 'default',
    title: 'Klasių statistika',
    statistics: null,
};

export default SchoolClassroomStatistic;
